import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { API_IMPORT_GUEST } from '../../core/redux/api/endpoints';
import { IAppState } from '../../core/redux/IAppState';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { GuestInfo } from '../guestInfo';
import { assoc } from '../../language/module';
import { updateGuest } from '../module';


const KEY_ACTION = ACTION_TYPE.IMPORT_GUEST_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.IMPORT_GUEST_REQUEST,
  ACTION_TYPE.IMPORT_GUEST_RESPONSE,
  ACTION_TYPE.IMPORT_GUEST_ERROR,
];


/**
 * Constructs an action that can be dispatched to import a guest's record from the IKON Central database to the resort-
 * specific database. This gives the resort access to the guest's information for analytics/marketing/tracking reasons.
 */
export const importGuestAction = (guest: GuestInfo) => makeApiAction(
  {
    method:   'POST',
    endpoint: API_IMPORT_GUEST,
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body: (state) =>  `{
    "userId":"${state.userState.userId}",
    "masterGuestId":"${guest.masterGuestId}",
    "resortAbbrv": "${state.selectedResort.resortAbbreviation}",
    "activeEntitlementsOnly": ${state.resortToolsConfig.activeEntitlementsOnly},
    "enableSyncCustomerMediaUpdate": ${state.resortToolsConfig.enableSyncCustomerMediaUpdate}
    }`,
    types:    ACTIONS.map(action => ({ type: action, meta: { guest } })) as RsaaTypes,
    bailout:  state => !!getLoading(state, guest.masterGuestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<undefined> = (state, action, error, isLoading, payload) => {
  const metaGuest = action.meta.guest as GuestInfo;
  const guestId   = metaGuest.masterGuestId as string;

  if (isLoading) {
    return assoc(
      'importSuccesses',
      R.filter(id => id !== guestId, state.importSuccesses),
      addLoading(state, guestId as string, ACTION_TYPE.IMPORT_GUEST_REQUEST),
    );
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    const message = action.payload?.response?.errors?.length > 0 ? action.payload?.response?.errors[0] : undefined;
    return addError(withBreaker, guestId, KEY_ACTION, error, { guest: metaGuest, errors: message });
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  const entitlementErrorCount = action.payload?.entitlementErrorCount || 0;
  const updatedGuest = updateGuest(noLoadingOrError, guestId, guest => assoc('entitlementErrorCount', entitlementErrorCount, guest));

  return assoc('importSuccesses', R.concat([ guestId ], updatedGuest.importSuccesses), updatedGuest);
};


/**
 * A function that creates new app state from Import Guest actions and previous app state.
 */
export const importGuestReducer = makeApiReducer<undefined>(ACTIONS, KEY_ACTION, reducer);
